/**
 * The NFL team chip — the one deliberate exception to the identity-colour rule.
 *
 * Everywhere else in the app, identity is a pale background and status is saturated foreground.
 * A club chip in a washed-out pastel is not recognisable as the club, so these use the team's
 * primary at full strength as the fill, the secondary as a 2px RIGHT border, and white ink.
 *
 * Three chips sit below the 4.5:1 contrast floor on purpose — CIN 4.4, LAR 2.8, NO 3.3 —
 * because dimming those fills far enough to pass stops them reading as Bengals orange, Rams
 * gold and Saints gold. The design accepts them and `tests/nflChips.test.ts` pins the list, so
 * a well-meaning "fix" fails a test instead of quietly greying the board.
 *
 * Colours and the accepted-trio come from the design bundle's 32-chip reference panel
 * (`FFL Draft Night.dc.html`), and every ratio there was re-derived from the hex values before
 * being trusted.
 */

import players from '../data/players-2026.json';
import { indexByName, lookupByName } from '../domain/playerNames.js';

/** fill = club primary, edge = secondary (the right border). Ink is white on all 32. */
export const NFL_CHIPS: Readonly<Record<string, { fill: string; edge: string }>> = {
  ARI: { fill: '#97233F', edge: '#FFB612' }, ATL: { fill: '#101820', edge: '#A71930' },
  BAL: { fill: '#241773', edge: '#9E7C0C' }, BUF: { fill: '#00338D', edge: '#C60C30' },
  CAR: { fill: '#0069A3', edge: '#101820' }, CHI: { fill: '#0B162A', edge: '#C83803' },
  CIN: { fill: '#D6480F', edge: '#000000' }, CLE: { fill: '#311D00', edge: '#FF3C00' },
  DAL: { fill: '#003594', edge: '#869397' }, DEN: { fill: '#C4400F', edge: '#0C2340' },
  DET: { fill: '#0076B6', edge: '#B0B7BC' }, GB: { fill: '#203731', edge: '#FFB612' },
  HOU: { fill: '#A71930', edge: '#03202F' }, IND: { fill: '#0A5FA8', edge: '#A2AAAD' },
  JAX: { fill: '#006778', edge: '#D7A22A' }, KC: { fill: '#E31837', edge: '#FFB81C' },
  LAC: { fill: '#00659E', edge: '#FFC20E' }, LAR: { fill: '#D68A00', edge: '#003594' },
  LV: { fill: '#5C6366', edge: '#101820' }, MIA: { fill: '#00727A', edge: '#FC4C02' },
  MIN: { fill: '#4F2683', edge: '#FFC62F' }, NE: { fill: '#002244', edge: '#C60C30' },
  NO: { fill: '#A8894A', edge: '#101820' }, NYG: { fill: '#0B2265', edge: '#A71930' },
  NYJ: { fill: '#125740', edge: '#C4C9CC' }, PHI: { fill: '#004C54', edge: '#A5ACAF' },
  PIT: { fill: '#101820', edge: '#FFB612' }, SEA: { fill: '#002244', edge: '#69BE28' },
  SF: { fill: '#AA0000', edge: '#B3995D' }, TB: { fill: '#B01212', edge: '#FF7900' },
  TEN: { fill: '#0C2340', edge: '#4B92DB' }, WAS: { fill: '#5A1414', edge: '#FFB612' },

  /*
   * THE CLUBS THAT MOVED. History goes back to 2003 and a 2010 Ram was a St. Louis Ram — the box
   * score says STL because that is what was true, and rewriting it to LAR would make the record
   * agree with today's map instead of with the season. Same for San Diego and Oakland. These
   * three appear only on historical screens; nothing current can produce them.
   */
  STL: { fill: '#002244', edge: '#B3995D' }, SD: { fill: '#002A5E', edge: '#FFC20E' },
  OAK: { fill: '#101820', edge: '#A5ACAF' },
};

/** The three fills accepted below 4.5:1. Pinned by test; do not "fix" (see file comment). */
export const ACCEPTED_LOW_CONTRAST = ['CIN', 'LAR', 'NO'] as const;

/** WCAG relative-luminance contrast, so the test derives ratios rather than trusting them. */
export function contrastRatio(hexA: string, hexB: string): number {
  const lum = (hex: string) => {
    const c = (i: number) => {
      const v = parseInt(hex.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * c(1) + 0.7152 * c(3) + 0.0722 * c(5);
  };
  const [a, b] = [lum(hexA), lum(hexB)];
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/* -- which team a player is on ------------------------------------------------------------- */

interface IndexedPlayer { n: string; p: string; t: string; b: number | null; st: string; last?: string }
const INDEX = (players as { players?: IndexedPlayer[] }).players
  ?? (players as unknown as IndexedPlayer[]);

const norm = (s: string) => s.normalize('NFKD').toLowerCase().replace(/[^a-z ]/g, '')
  .replace(/\b(jr|sr|ii|iii|iv)\b/g, '').replace(/\s+/g, ' ').trim();

/**
 * Every week the season has a bye in, ascending — the x axis of the 8a bye chart.
 *
 * Taken from the index's own team→week table rather than hardcoded, because the NFL moves the
 * window: 2026 runs 5 to 14 with nothing in 12, and a chart that assumed a contiguous run would
 * quietly draw a week that does not exist. An empty week inside the range keeps its column; the
 * flat rule under it is the answer "nobody".
 */
export const BYE_WEEKS: readonly number[] = (() => {
  const byTeam = (players as { byes?: Record<string, number> }).byes ?? {};
  const weeks = Object.values(byTeam).filter((w): w is number => typeof w === 'number');
  if (weeks.length === 0) return [];
  const lo = Math.min(...weeks);
  const hi = Math.max(...weeks);
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
})();

/**
 * The league's rosters name a defense by city ("Buffalo"); the player index names it by
 * nickname ("Bills DEF"). Same standing translation problem the transaction log had, solved
 * the same way: one table, city form → team code.
 */
const DEF_CITY: Readonly<Record<string, string>> = {
  arizona: 'ARI', atlanta: 'ATL', baltimore: 'BAL', buffalo: 'BUF', carolina: 'CAR',
  chicago: 'CHI', cincinnati: 'CIN', cleveland: 'CLE', dallas: 'DAL', denver: 'DEN',
  detroit: 'DET', 'green bay': 'GB', houston: 'HOU', indianapolis: 'IND',
  jacksonville: 'JAX', 'kansas city': 'KC', 'las vegas': 'LV', 'los angeles': 'LAR',
  chargers: 'LAC', miami: 'MIA', minnesota: 'MIN', 'new england': 'NE',
  'new orleans': 'NO', giants: 'NYG', jets: 'NYJ', philadelphia: 'PHI', pittsburgh: 'PIT',
  'san francisco': 'SF', seattle: 'SEA', 'tampa bay': 'TB', tennessee: 'TEN',
  washington: 'WAS',
};

/*
 * The alias table used to live here, known only to this file — so the ADP board looked up "Nick
 * Singleton", missed, and reported a mandatory farm graduate as having no market value. One table,
 * in the domain, consulted by everything: `domain/playerNames.ts`.
 */

const BY_NAME = indexByName(INDEX, (p) => p.n);

/**
 * What the player index knows about a rostered name, or null when it knows nothing.
 *
 * `team` is null for a free agent — the index carries `t: ''` with `st: 'FA'` and the club he
 * was last on — and for the retired. That null is information, not a gap: on a keeper screen,
 * "this candidate has no NFL team right now" is exactly the thing a manager about to spend a
 * pick on him should see. The chip renders it as a grey FA/RET marker rather than nothing.
 */
export interface NflInfo {
  team: string | null;
  bye: number | null;
  /** ACT, FA, PS, IR, RET, EXE or CUT — the index's own status vocabulary. */
  status: string;
  /** For a free agent, the club he was last on. */
  last: string | null;
  /** His position in the index, for screens whose own record has none (the archive). */
  pos: string | null;
}

export function nflInfoOf(player: string): NflInfo | null {
  const k = norm(player);
  const hit = lookupByName(BY_NAME, player) ?? (() => {
    const city = DEF_CITY[k];
    return city ? INDEX.find((p) => p.p === 'DEF' && p.t === city) : undefined;
  })();
  if (!hit) return null;
  return {
    team: NFL_CHIPS[hit.t] ? hit.t : null,
    bye: hit.b ?? null,
    status: hit.st,
    last: hit.last ?? null,
    pos: hit.p ?? null,
  };
}

/* -- the chip ------------------------------------------------------------------------------ */

export function NflChip({ team, title }: { team: string; title?: string }) {
  const c = NFL_CHIPS[team];
  if (!c) return null;
  return (
    <span
      className="nflchip"
      title={title ?? team}
      style={{ background: c.fill, borderRightColor: c.edge }}
    >
      {team}
    </span>
  );
}

/**
 * The whole chip slot for a player: his club, or the reason he has no club. A free agent reads
 * FA (with his last team on the tooltip) and a retired player reads RET — grey, because it is
 * a fact about him and not a judgement, but visible, because it is the kind of fact that
 * changes whether he is worth a keeper slot at all.
 */
export function NflMark({ player }: { player: string }) {
  const info = nflInfoOf(player);
  if (!info) return null;
  if (info.team) {
    return <NflChip team={info.team} title={info.bye ? `${info.team} · bye ${info.bye}` : info.team} />;
  }
  const label = info.status === 'RET' ? 'RET' : info.status === 'FA' ? 'FA' : null;
  if (!label) return null;
  return (
    <span className="nflchip none"
      title={info.status === 'RET' ? 'Retired' : `Free agent${info.last ? ` — last with ${info.last}` : ''}`}>
      {label}
    </span>
  );
}
